/**
 * Random AI Strategy (v0)
 *
 * Simplest AI implementation that selects random valid moves.
 * Used for testing and as a baseline for other AI strategies.
 * Should respond almost instantly (<100ms).
 */

import { AIDifficulty, PlayerColor, BoardPosition } from '@/types/game-types';
import type { GameState } from '@/core/models/GameState';
import { AIStrategyBase, type AIMoveResult, AIUtils } from '../ai-strategy-base';

/**
 * Random AI implementation
 */
export class RandomAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.RANDOM;
  public readonly name = 'Random AI';
  public readonly description = 'Selects random valid moves with no strategy';

  /**
   * Select a random valid move
   */
  protected async selectMoveImpl(
    gameState: GameState,
    _player: PlayerColor,
    _timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();

    // Get all valid moves
    const validMoves = Array.from(gameState.validMoves.keys());

    if (validMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    // Add tiny random delay to simulate thinking (but stay under 100ms)
    const thinkingDelay = Math.random() * 50; // 0-50ms
    await new Promise(resolve => setTimeout(resolve, thinkingDelay));

    // Select random move
    const randomIndex = Math.floor(Math.random() * validMoves.length);
    const selectedPosition = validMoves[randomIndex];

    // Calculate basic evaluation score (just for metrics)
    const evaluation = this.evaluateMove(gameState, selectedPosition!, _player);

    return {
      position: selectedPosition!,
      confidence: 0.1, // Very low confidence since it's random
      evaluation,
      thinkingTime: Date.now() - startTime,
      depth: 0, // No search depth
      nodesEvaluated: validMoves.length, // Only evaluated immediate moves
    };
  }

  /**
   * Simple move evaluation for metrics (not used for selection)
   */
  private evaluateMove(
    gameState: GameState,
    position: BoardPosition,
    _player: PlayerColor,
  ): number {
    // Basic evaluation based on position value and flips
    const flippedPositions = gameState.validMoves.get(position) || [];
    let score = 0;

    // Position value
    if ([0, 7, 56, 63].includes(position)) {
      score += 25; // Corner
    } else if (this.isEdgePosition(position)) {
      score += 6; // Edge
    } else if ([9, 14, 49, 54].includes(position)) {
      score -= 10; // X-square (dangerous)
    }

    // Flipped pieces
    score += flippedPositions.length;

    // Add small random component for variety
    score += AIUtils.addJitter(0, 0.5);

    return score;
  }

  /**
   * Check if position is on the edge
   */
  private isEdgePosition(position: BoardPosition): boolean {
    const row = Math.floor(position / 8);
    const col = position % 8;
    return row === 0 || row === 7 || col === 0 || col === 7;
  }
}

/**
 * Slightly improved random AI that avoids obviously bad moves
 */
export class SmartRandomAI extends AIStrategyBase {
  public readonly difficulty = AIDifficulty.RANDOM;
  public readonly name = 'Smart Random AI';
  public readonly description = 'Random moves but avoids obviously terrible positions';

  protected async selectMoveImpl(
    gameState: GameState,
    _player: PlayerColor,
    _timeLimit: number,
  ): Promise<AIMoveResult> {
    const startTime = Date.now();

    const validMoves = Array.from(gameState.validMoves.keys());

    if (validMoves.length === 0) {
      throw new Error('No valid moves available');
    }

    // Categorize moves
    const cornerMoves = validMoves.filter(pos => [0, 7, 56, 63].includes(pos));
    const safeEdgeMoves = validMoves.filter(pos =>
      this.isEdgePosition(pos) && ![0, 7, 56, 63].includes(pos) && !this.isXSquare(pos),
    );
    const xSquareMoves = validMoves.filter(pos => [9, 14, 49, 54].includes(pos));
    const normalMoves = validMoves.filter(pos =>
      !cornerMoves.includes(pos) &&
      !safeEdgeMoves.includes(pos) &&
      !xSquareMoves.includes(pos),
    );

    let candidateMoves: BoardPosition[];
    let moveType: string;

    // Selection priority: Corners > Safe edges > Normal > X-squares (last resort)
    if (cornerMoves.length > 0) {
      candidateMoves = cornerMoves;
      moveType = 'corner';
    } else if (safeEdgeMoves.length > 0) {
      candidateMoves = safeEdgeMoves;
      moveType = 'safe_edge';
    } else if (normalMoves.length > 0) {
      candidateMoves = normalMoves;
      moveType = 'normal';
    } else {
      candidateMoves = xSquareMoves;
      moveType = 'x_square';
    }

    // Add thinking delay
    const thinkingDelay = Math.random() * 75; // 0-75ms
    await new Promise(resolve => setTimeout(resolve, thinkingDelay));

    // Select random from candidates
    const randomIndex = Math.floor(Math.random() * candidateMoves.length);
    const selectedPosition = candidateMoves[randomIndex];

    // Evaluate selected move
    const evaluation = this.evaluateMove(gameState, selectedPosition!, _player);

    // Higher confidence for better move types
    const confidence = this.getConfidenceForMoveType(moveType);

    return {
      position: selectedPosition!,
      confidence,
      evaluation,
      thinkingTime: Date.now() - startTime,
      depth: 1, // One level of categorization
      nodesEvaluated: validMoves.length,
    };
  }

  private evaluateMove(
    gameState: GameState,
    position: BoardPosition,
    _player: PlayerColor,
  ): number {
    const flippedPositions = gameState.validMoves.get(position) || [];
    let score = 0;

    // Position value
    if ([0, 7, 56, 63].includes(position)) {
      score += 25;
    } else if (this.isEdgePosition(position)) {
      score += 6;
    } else if ([9, 14, 49, 54].includes(position)) {
      score -= 10;
    }

    // Flipped pieces
    score += flippedPositions.length;

    return score;
  }

  private isEdgePosition(position: BoardPosition): boolean {
    const row = Math.floor(position / 8);
    const col = position % 8;
    return row === 0 || row === 7 || col === 0 || col === 7;
  }

  private isXSquare(position: BoardPosition): boolean {
    return [9, 14, 49, 54].includes(position);
  }

  private getConfidenceForMoveType(moveType: string): number {
    switch (moveType) {
      case 'corner': return 0.8;
      case 'safe_edge': return 0.6;
      case 'normal': return 0.3;
      case 'x_square': return 0.1;
      default: return 0.2;
    }
  }
}

/**
 * Factory function to create random AI variants
 */
export const createRandomAI = {
  /**
   * Pure random selection
   */
  pure(): RandomAI {
    return new RandomAI();
  },

  /**
   * Smart random (avoids obviously bad moves)
   */
  smart(): SmartRandomAI {
    return new SmartRandomAI();
  },
};